import React, { useEffect, useMemo, useCallback } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Tabs,
  Tab,
  Button,
  IconButton,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import "./CallLogDialog.scss";

import { useDispatch, useSelector } from "react-redux";
import ProfessionalInfoTab from "./ProfessionalInfoTab";

import { useAzureSpeechToText } from "../../utils/hooks/useAzureSpeechToText";
import DetailingPriorityTab from "./DetailingPriorityTab";
import PromotionalTab from "./PromotionalTab";
import { submitCallLog } from "../../redux/slice/callLogSlice";
import { showSnackbar } from "../../redux/slice/snackbar";
import {
  buildDetailingDataFromPayload,
  prefillRepTerritoryFromPayload,
  buildCallLogSubmitBody,
} from "./callLogDialogUtils";
import { useCallLogDialogValidations } from "./hooks/useCallLogDialogValidations";
import { useCallLogDetailingFlow } from "./hooks/useCallLogDetailingFlow";

const PROMOTIONAL_PRODUCTS = Object.freeze([]);
const PROMOTIONAL_ITEMS_BY_PRODUCT = Object.freeze({});
const SUPPORTED_SPEECH_LANGS = Object.freeze(["en-US"]);
const TAB_CATEGORIES = Object.freeze([
  "Professional Information",
  "Detailing Priority",
  "Promotional",
]);

const INITIAL_FORM = Object.freeze({
  salesRepName: "",
  territoryName: "",
  callDateTime: "",
  callDuration: "",
  callType: "normal",
  accountName: "",
  institutionId: "",
  attendees: [],
  address: "",
  subject: "",
});

function CallLogDialog({ open, onClose, callData }) {
  const [tabValue, setTabValue] = React.useState(0);
  const [formData, setFormData] = React.useState(() => ({ ...INITIAL_FORM }));

  const submitting = useSelector((state) => state.callLog.submitting);
  const speechConfigAzure = useSelector(
    (state) => state.speechConfig.speechConfig,
  );

  const dispatch = useDispatch();

  const payload = useMemo(() => callData || {}, [callData]);
  const hcps = useMemo(() => payload?.HCPs || [], [payload]);
  const hcos = useMemo(
    () =>
      (payload?.HCOs || []).filter(
        (x) => x?.InstitutionID && x?.InstitutionName,
      ),
    [payload],
  );

  const {
    detailQuery,
    setDetailQuery,
    queryRef,
    isFetchingDetail,
    detailError,
    setDetailError,
    detailApiData,
    setDetailApiData,
    isPromptSubmitted,
    selectedProducts,
    setSelectedProducts,
    selectedKeyMessagesByProduct,
    selectedKeyMessages,
    setSelectedKeyMessages,
    setKeyMessagesByProductAndFlatten,
    handleSendPrompt,
    resetDetailingState,
  } = useCallLogDetailingFlow(dispatch, payload);

  const tabValidations = useCallLogDialogValidations({
    formData,
    selectedProducts,
    selectedKeyMessagesByProduct,
  });

  useEffect(() => {
    if (!open) return;

    const { salesRepName, territoryName } =
      prefillRepTerritoryFromPayload(payload);
    setFormData((prev) => ({
      ...prev,
      salesRepName,
      territoryName,
    }));

    try {
      const built = buildDetailingDataFromPayload(payload);
      setDetailApiData(built);
    } catch (err) {
      console.error("buildDetailingDataFromPayload failed on open:", err);
      setDetailApiData(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, callData]);

  const handleChange = useCallback((field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  }, []);

  const handleDoctorSelect = useCallback((doctor) => {
    setFormData((prev) => ({
      ...prev,
      accountName: doctor?.DoctorID || "",
      address: doctor?.DoctorAddress || "",
    }));
  }, []);

  const { isListening, toggleListening, stopListening } = useAzureSpeechToText(
    {
      speechKey: speechConfigAzure?.speech_key,
      speechRegion: speechConfigAzure?.speech_region,
      supportedLanguages: SUPPORTED_SPEECH_LANGS,
      onFinalResult: (text) => {
        const updated = (queryRef.current + " " + text).trim();
        setDetailQuery(updated);
        queryRef.current = updated;
      },
      onError: (msg) => {
        setDetailError(msg || "Speech-to-text error.");
      },
    },
  );

  const handleMicClickForPrompt = useCallback(() => {
    setDetailError("");
    toggleListening();
  }, [setDetailError, toggleListening]);

  const [promotionalItems, setPromotionalItems] = React.useState([
    { productName: "", item: "", qty: 1 },
  ]);

  const buildSubmitBody = useCallback(
    () =>
      buildCallLogSubmitBody({
        detailQuery,
        formData,
        selectedProducts,
        selectedKeyMessagesByProduct,
        promotionalItems,
        detailApiData,
        payload,
        hcps,
        hcos,
      }),
    [
      detailQuery,
      formData,
      selectedProducts,
      selectedKeyMessagesByProduct,
      promotionalItems,
      detailApiData,
      payload,
      hcps,
      hcos,
    ],
  );

  const handleSubmit = useCallback(async () => {
    try {
      const body = buildSubmitBody();

      const cd = body?.professionalInfo?.callDuration;
      body.professionalInfo.callDuration =
        cd === "" || cd == null ? 0 : parseInt(String(cd), 10) || 0;

      const resp = await dispatch(submitCallLog(body)).unwrap();

      console.log("Submit success:", resp);

      dispatch(
        showSnackbar({
          message: "Call log submitted successfully",
          severity: "success",
        }),
      );

      onClose();
    } catch (e) {
      console.error("Submit failed:", e);
      dispatch(
        showSnackbar({
          message: typeof e === "string" ? e : "Submit failed",
          severity: "error",
        }),
      );
    }
  }, [buildSubmitBody, dispatch, onClose]);

  const handleNext = useCallback(() => {
    setTabValue((v) => (v === 0 ? 1 : v));
  }, []);

  const handlePrevious = useCallback(() => {
    setTabValue((prev) => (prev > 0 ? prev - 1 : prev));
  }, []);

  useEffect(() => {
    if (!open) {
      setTabValue(0);
      setFormData({ ...INITIAL_FORM });
      resetDetailingState();
      setPromotionalItems([{ productName: "", item: "", qty: 1 }]);
      stopListening();
    }
  }, [open, resetDetailingState, stopListening]);

  const isSubmitEnabled =
    tabValidations.professionalInfo && tabValidations.detailingPriority;

  return (
    <Dialog
      open={open}
      onClose={(event, reason) => {
        if (reason === "backdropClick" || reason === "escapeKeyDown") return;
        onClose();
      }}
      fullWidth
      PaperProps={{
        sx: { maxWidth: "700px", borderRadius: 2, minHeight: "500px" },
      }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          pb: 2,
        }}
      >
        <Box component="span" sx={{ fontSize: "20px", fontWeight: 600 }}>
          Call Log Information
        </Box>
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <Box sx={{ px: 3, pt: 2 }}>
        <Box className="calllog-tabs-pill">
          <Tabs
            value={tabValue}
            onChange={(_, v) => setTabValue(v)}
            variant="standard"
            scrollButtons={false}
            TabIndicatorProps={{ style: { display: "none" } }}
          >
            {TAB_CATEGORIES.map((cat, idx) => (
              <Tab
                key={cat}
                label={cat}
                disabled={idx === 2 || (idx !== 0 && !isPromptSubmitted)}
              />
            ))}
          </Tabs>
        </Box>
      </Box>

      <DialogContent sx={{ px: 3, py: 3 }} className="calllog-form">
        {tabValue === 0 && (
          <ProfessionalInfoTab
            formData={formData}
            onChange={handleChange}
            hcps={hcps}
            hcos={hcos}
            onDoctorSelect={handleDoctorSelect}
            payload={payload}
          />
        )}

        {tabValue === 1 && (
          <DetailingPriorityTab
            isPromptSubmitted={isPromptSubmitted}
            detailApiData={detailApiData}
            selectedProducts={selectedProducts}
            setSelectedProducts={setSelectedProducts}
            selectedKeyMessages={selectedKeyMessages}
            setSelectedKeyMessages={setSelectedKeyMessages}
            selectedKeyMessagesByProduct={selectedKeyMessagesByProduct}
            setSelectedKeyMessagesByProduct={setKeyMessagesByProductAndFlatten}
            detailQuery={detailQuery}
            setDetailQuery={setDetailQuery}
            onSendPrompt={handleSendPrompt}
            isFetchingDetail={isFetchingDetail}
            detailError={detailError}
            isListening={isListening}
            onMicClick={handleMicClickForPrompt}
          />
        )}

        {tabValue === 2 && (
          <PromotionalTab
            isPromptSubmitted={isPromptSubmitted}
            promotionalItems={promotionalItems}
            setPromotionalItems={setPromotionalItems}
            promotionalProducts={PROMOTIONAL_PRODUCTS}
            promotionalItemsByProduct={PROMOTIONAL_ITEMS_BY_PRODUCT}
          />
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 3, pt: 2 }}>
        <Button
          onClick={onClose}
          variant="outlined"
          sx={{
            textTransform: "none",
            px: 4,
            py: 1,
            borderColor: "#ccc",
            color: "#333",
            "&:hover": { borderColor: "#999", backgroundColor: "#f5f5f5" },
          }}
        >
          Cancel
        </Button>

        {tabValue > 0 && (
          <Button
            onClick={handlePrevious}
            variant="outlined"
            sx={{
              textTransform: "none",
              px: 4,
              py: 1,
              borderColor: "#ccc",
              color: "#333",
              "&:hover": { borderColor: "#999", backgroundColor: "#f5f5f5" },
            }}
          >
            Previous
          </Button>
        )}

        {tabValue === 0 && (
          <Button
            onClick={handleNext}
            disabled={!tabValidations.professionalInfo}
            variant="contained"
            sx={{
              textTransform: "none",
              px: 4,
              py: 1,
              backgroundColor: "#5f8aa5",
              "&:hover": { backgroundColor: "#4a7390" },
            }}
          >
            Next
          </Button>
        )}

        {tabValue === 1 && (
          <Button
            onClick={handleSubmit}
            variant="contained"
            disabled={!isSubmitEnabled}
            sx={{
              textTransform: "none",
              px: 4,
              py: 1,
              backgroundColor: "#5f8aa5",
              "&:hover": { backgroundColor: "#4a7390" },
            }}
          >
            {submitting ? "Submitting..." : "Submit"}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}

export default CallLogDialog;
